export type ParsedClassCode = {
  segment: "fundamental" | "medio";
  level: number;
  classLetter: string;
  grade: string;
  label: string;
};

/** Interpreta os códigos de turma usados na planilha institucional. */
export function parseClassCode(value: string): ParsedClassCode | null {
  const match = /^(EFun|EMed)-([1-9])(?:AM)?([A-Z])$/i.exec(value.trim());
  if (!match) return null;

  const [, prefix, levelText, letter] = match;
  const level = Number(levelText);
  const classLetter = letter.toUpperCase();
  if (prefix.toLowerCase() === "efun") {
    return {
      segment: "fundamental",
      level,
      classLetter,
      grade: `${level}º ano`,
      label: `${level}º ano ${classLetter} (EF)`,
    };
  }
  if (level > 3) return null;
  return {
    segment: "medio",
    level,
    classLetter,
    grade: `${level}ª série EM`,
    label: `${level}º série ${classLetter} (EM)`,
  };
}

/** Altera apenas o texto exibido. O código original continua sendo a chave da turma. */
export function formatClassName(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = parseClassCode(value);
  if (!parsed) return value;
  return parsed.label;
}

/** Formata códigos citados dentro de títulos e mensagens já persistidos. */
export function formatClassCodesInText(value: string | null | undefined): string {
  if (!value) return "";
  return value.replace(/\b(?:EFun|EMed)-[1-9](?:AM)?[A-Z]\b/gi, (code) => formatClassName(code));
}

const classSortKey = (value: string) => {
  const coded = parseClassCode(value);
  if (coded) return { segment: coded.segment === "fundamental" ? 0 : 1, level: coded.level, letter: coded.classLetter };
  const fundamental = /^([1-9])\s*[ºo]\s*(?:ano\s*)?([A-Z]+)$/i.exec(value);
  if (fundamental) return { segment: 0, level: Number(fundamental[1]), letter: fundamental[2].toUpperCase() };
  const medio = /^([1-3])\s*[ªa]\s*(?:série\s*)?([A-Z]+)$/i.exec(value);
  if (medio) return { segment: 1, level: Number(medio[1]), letter: medio[2].toUpperCase() };
  return { segment: 2, level: 0, letter: value };
};

export function compareClassNames(a: string, b: string): number {
  const left = classSortKey(a);
  const right = classSortKey(b);
  return left.segment - right.segment || left.level - right.level ||
    left.letter.localeCompare(right.letter, "pt-BR", { numeric: true }) ||
    a.localeCompare(b, "pt-BR", { numeric: true });
}
